// repo/primary-care-connect/frontend/src/pages/Portal/FamilyDoctor/PatientRequestDetails/PatientRequestDetailsPage.tsx
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "../NewPatientRequests/NewPatientRequestsPage.css";
import "./PatientRequestDetailsPage.css";

import PatientRequestActivityPanel from "../../components/PatientRequestActivityPanel";
import PatientRequestAttachmentOutcomeCard from "../../components/PatientRequestAttachmentOutcomeCard";

import {
  ApiError,
  DecisionEmailPreview,
  DecisionValue,
  FinalizeAction,
  FinalizeEmailPreview,
  RequestDetail,
  decideFamilyDoctorRequest,
  finalizeFamilyDoctorRequest,
  getFamilyDoctorRequestDetails,
  previewFamilyDoctorRequestDecision,
  previewFamilyDoctorRequestFinalize,
} from "../../../../api/requests";

const MEET_AND_GREET_BACKLOG_LIMIT_CODE =
  "MEET_AND_GREET_BACKLOG_LIMIT_REACHED";

type UiTag = {
  key: string;
  label: string;
  title?: string;
  className?: string;
};

function asText(value: unknown): string {
  return String(value ?? "").trim();
}

function asLower(value: unknown): string {
  return asText(value).toLowerCase();
}

function formatDateTime(value: unknown): string {
  const raw = asText(value);
  if (!raw) return "—";

  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatDateOnly(value: unknown): string {
  const raw = asText(value);
  if (!raw) return "—";

  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  if (m) {
    const yyyy = Number(m[1]);
    const mm = Number(m[2]);
    const dd = Number(m[3]);

    if (Number.isFinite(yyyy) && Number.isFinite(mm) && Number.isFinite(dd)) {
      const utc = new Date(Date.UTC(yyyy, mm - 1, dd));
      return new Intl.DateTimeFormat(undefined, { timeZone: "UTC" }).format(
        utc,
      );
    }

    return raw;
  }

  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function normalizeStatus(status: unknown): string {
  const raw = asText(status);
  if (!raw) return "";

  const key = raw.toLowerCase();
  if (key === "in_review") return "InReview";
  if (key === "notaccepted") return "Declined";
  if (key === "attachment_complete" || key === "attachmentcomplete") {
    return "AttachmentComplete";
  }
  if (key === "closedpatientwithdrew") return "ClosedPatientWithdrew";
  if (key === "closedacceptedelsewhere") return "ClosedAcceptedElsewhere";
  if (key === "closednoshow") return "ClosedNoShow";
  if (key === "closedclinicdeclinedaftermeetgreet") {
    return "ClosedClinicDeclinedAfterMeetGreet";
  }

  return raw;
}

function statusDisplayLabel(status: unknown): string {
  const normalized = normalizeStatus(status);
  const key = normalized.toLowerCase();

  if (key === "submitted" || key === "inreview") return "Submitted";
  if (key === "waitlist") return "Waitlisted";
  if (key === "accepted") return "Invited for Meet & Greet";
  if (key === "attachmentcomplete") return "Attachment Complete";
  if (key === "declined") return "Declined by clinic";
  if (key === "closedpatientwithdrew") return "Closed — Patient Withdrew";
  if (key === "closedacceptedelsewhere") return "Closed — Accepted Elsewhere";
  if (key === "closednoshow") return "Closed — Meet & Greet Not Completed";
  if (key === "closedclinicdeclinedaftermeetgreet") {
    return "Closed — Clinic Declined After Meet & Greet";
  }
  if (key === "cancelled") return "Cancelled by patient";
  if (key === "expired") return "Auto-Expired";

  return normalized || "Unknown";
}

function statusPillClass(status: unknown): string {
  const key = normalizeStatus(status).toLowerCase();

  if (key === "submitted" || key === "inreview") {
    return "ocf-status ocf-status--pending";
  }
  if (key === "accepted") return "ocf-status ocf-status--accepted";
  if (key === "waitlist") return "ocf-status ocf-status--waitlist";
  if (
    key === "declined" ||
    key === "cancelled" ||
    key === "expired" ||
    key === "closedclinicdeclinedaftermeetgreet"
  ) {
    return "ocf-status ocf-status--declined";
  }
  if (
    key === "attachmentcomplete" ||
    key === "closedpatientwithdrew" ||
    key === "closedacceptedelsewhere" ||
    key === "closednoshow"
  ) {
    return "ocf-status ocf-status--closed";
  }

  return "ocf-status";
}

function normalizePriorityLabel(value: unknown): string | null {
  const raw = asText(value);
  if (!raw) return null;

  const key = raw.toLowerCase();
  if (key === "urgent") return "Urgent";
  if (key === "high") return "High";
  if (key === "medium") return "Medium";
  if (key === "low") return "Low";
  if (key === "routine") return "Routine";

  return null;
}

function priorityPillClass(priority: string): string {
  const key = priority.toLowerCase();
  if (key === "urgent") return "ocf-priority-pill ocf-priority-pill--urgent";
  if (key === "high") return "ocf-priority-pill ocf-priority-pill--high";
  if (key === "medium") return "ocf-priority-pill ocf-priority-pill--medium";
  if (key === "low") return "ocf-priority-pill ocf-priority-pill--low";
  return "ocf-priority-pill ocf-priority-pill--routine";
}

function getNavigatorPriority(detail: RequestDetail | null): string {
  if (!detail) return "Routine";

  const d = detail as any;
  const candidates = [
    d.navigatorPriorityLevel,
    d.NavigatorPriorityLevel,
    d.priorityLevel,
    d.PriorityLevel,
    d.navigator?.priorityLevel,
    d.navigator?.PriorityLevel,
    d.navigator?.navigatorPriorityLevel,
    d.navigator?.NavigatorPriorityLevel,
    d.request?.navigatorPriorityLevel,
    d.request?.NavigatorPriorityLevel,
    d.request?.priorityLevel,
    d.request?.PriorityLevel,
  ];

  for (const candidate of candidates) {
    const normalized = normalizePriorityLabel(candidate);
    if (normalized) return normalized;
  }

  return "Routine";
}

function isNavigatorSourceType(sourceType: unknown): boolean {
  const key = asText(sourceType);
  return (
    key === "NavigatorPublicRequest" ||
    key === "NavigatorUpload" ||
    key === "OHT" ||
    key === "FutureHCC" ||
    key === "ManualNavigatorEntry"
  );
}

function isNavigatorAssisted(detail: RequestDetail | null): boolean {
  if (!detail) return false;

  const d = detail as any;
  const navigatorCaseId =
    d.navigator?.navigatorCaseId ??
    d.navigator?.navigatorCaseID ??
    d.navigatorCaseId ??
    d.NavigatorCaseID ??
    null;

  if (navigatorCaseId !== null && navigatorCaseId !== undefined) return true;
  if (d.navigator?.isNavigatorAssisted) return true;
  if (isNavigatorSourceType(detail.request?.sourceType)) return true;

  const sourceLabel = asLower(
    detail.request?.sourceLabel || detail.navigator?.sourceLabel,
  );
  const source = asLower(detail.request?.source);

  return sourceLabel.includes("navigator") || source.includes("navigator");
}

function sourceDisplayLabel(detail: RequestDetail | null): string {
  if (!detail) return "—";

  return isNavigatorAssisted(detail) ? "Navigator" : "Public";
}

function sourcePillClass(detail: RequestDetail | null): string {
  return isNavigatorAssisted(detail)
    ? "ocf-source-pill ocf-source-pill--navigator"
    : "ocf-source-pill ocf-source-pill--public";
}

function patientDisplayName(patient: any): string {
  if (!patient) return "Unnamed patient";

  const first = asText(patient.firstName);
  const last = asText(patient.lastName);
  const composed = [first, last].filter(Boolean).join(" ").trim();
  if (composed) return composed;

  const legacy = asText(patient.fullName || patient.name);
  return legacy || "Unnamed patient";
}

function formatRequestFor(value: unknown): string {
  const raw = asText(value);
  if (!raw) return "—";

  const key = raw.toLowerCase();
  if (key === "myself") return "Myself";
  if (key === "mychild" || key === "minor" || key === "minorchild") {
    return "My Minor Child";
  }
  if (key === "myfamily" || key === "family" || key === "adultfamilymember") {
    return "My Adult Family Member";
  }

  return raw;
}

function decisionActionLabel(decision: DecisionValue): string {
  if (decision === "Accepted") return "Invite for Meet & Greet";
  if (decision === "Waitlist") return "Waitlist";
  return "Decline";
}

function decisionConfirmLabel(decision: DecisionValue): string {
  if (decision === "Accepted") return "Confirm & Send Invitation";
  if (decision === "Waitlist") return "Confirm Waitlist & Send Email";
  return "Confirm Decline & Send Email";
}

function finalizeActionRequiresEmailPreview(action: FinalizeAction): boolean {
  return action === "MovedToWaitlist";
}

function finalizeActionLabel(action: FinalizeAction): string {
  if (action === "MovedToWaitlist") return "Move back to waitlist";
  if (action === "AttachmentComplete") return "Attachment Complete";
  if (action === "PatientWithdrew") return "Patient withdrew / declined";
  if (action === "PatientAcceptedElsewhere") return "Patient accepted elsewhere";
  if (action === "NoShow") return "Meet & Greet was not completed";
  if (action === "ClinicDeclinedAfterMeetAndGreet") {
    return "Clinic declined after Meet & Greet";
  }

  return "Finalize request";
}

function finalizeConfirmLabel(action: FinalizeAction): string {
  if (action === "MovedToWaitlist") {
    return "Confirm Move to Waitlist & Send Email";
  }

  return "Confirm Outcome";
}

function decisionSavedMessage(decision: DecisionValue): string {
  if (decision === "Accepted") return "Meet and greet invitation sent.";
  if (decision === "Waitlist") return "Waitlist email sent.";
  return "Decline email sent.";
}

function finalizeSavedMessage(
  action: FinalizeAction,
  apiMessage?: string | null,
): string {
  const message = asText(apiMessage);
  if (message) return message;

  if (action === "AttachmentComplete") return "Attachment marked complete.";
  if (action === "PatientWithdrew") {
    return "Request closed: patient withdrew or declined.";
  }
  if (action === "PatientAcceptedElsewhere") {
    return "Request closed: patient accepted elsewhere.";
  }
  if (action === "NoShow") {
    return "Request closed: meet and greet was not completed.";
  }
  if (action === "ClinicDeclinedAfterMeetAndGreet") {
    return "Request closed: clinic declined after meet and greet.";
  }
  if (action === "MovedToWaitlist") return "Request moved to waitlist.";

  return "Request closed after meet and greet invitation.";
}

function decisionErrorMessage(error: unknown, fallback: string): string {
  if (!(error instanceof ApiError)) return fallback;

  const code = asText(error.payload?.code);
  if (code !== MEET_AND_GREET_BACKLOG_LIMIT_CODE) {
    return error.message || fallback;
  }

  const countRaw = error.payload?.meetAndGreetBacklogCount;
  const limitRaw = error.payload?.limit;
  const count = Number.isFinite(Number(countRaw)) ? Number(countRaw) : null;
  const limit = Number.isFinite(Number(limitRaw)) ? Number(limitRaw) : null;

  if (count !== null && limit !== null) {
    return `You have reached your Meet & Greet invitation limit (${count}/${limit}). Please clear your existing backlog before inviting more patients.`;
  }

  return (
    error.message ||
    "You have reached your Meet & Greet invitation limit. Please clear your existing backlog before inviting more patients."
  );
}

function isFinalized(detail: RequestDetail | null): boolean {
  if (!detail) return false;

  const key = normalizeStatus(detail.status).toLowerCase();
  if (detail.timeline.cancelledAt || detail.timeline.autoExpiredAt) return true;

  return (
    key === "declined" ||
    key === "closedacceptedelsewhere" ||
    key === "closedpatientwithdrew" ||
    key === "closednoshow" ||
    key === "closedclinicdeclinedaftermeetgreet" ||
    key === "cancelled" ||
    key === "expired" ||
    key === "attachmentcomplete"
  );
}

function getServerTag(detail: RequestDetail | null, keys: string[]): any | null {
  const tags = Array.isArray((detail as any)?.priorityTags)
    ? ((detail as any).priorityTags as any[])
    : [];
  const wanted = new Set(keys.map((key) => key.toLowerCase()));

  return tags.find((tag) => wanted.has(asLower(tag?.key || tag?.Key))) || null;
}

function buildTopTags(detail: RequestDetail | null): UiTag[] {
  if (!detail) return [];

  const tags: UiTag[] = [];
  const unattachedTag = getServerTag(detail, ["unattached_moh", "unattached"]);
  const attachedTag = getServerTag(detail, ["attached_moh", "attached"]);

  if (unattachedTag) {
    tags.push({
      key: "patient-baseline",
      label: "Unattached",
      title:
        asText(unattachedTag.basis || unattachedTag.Basis) ||
        "MOH-aligned unattached patient declaration.",
      className: "ocf-baseline-pill ocf-baseline-pill--unattached",
    });
  } else if (attachedTag) {
    tags.push({
      key: "patient-baseline",
      label: "Attached / Switching",
      title:
        asText(attachedTag.basis || attachedTag.Basis) ||
        "Patient indicated they currently have a family doctor but want to switch.",
      className: "ocf-baseline-pill ocf-baseline-pill--attached",
    });
  } else {
    tags.push({
      key: "patient-baseline",
      label: "Baseline unknown",
      title: "No MOH baseline tag was returned for this request.",
      className: "ocf-baseline-pill ocf-baseline-pill--unknown",
    });
  }

  tags.push({
    key: "coverage",
    label: asText(detail.request?.coverageType?.name) || "Coverage unknown",
    title: "Coverage type from the patient request.",
    className: "ocf-detail-tag ocf-detail-tag--coverage",
  });

  const withinFsaTag = getServerTag(detail, ["within_catchment"]);
  const outsideFsaTag = getServerTag(detail, ["outside_catchment"]);

  if (withinFsaTag) {
    tags.push({
      key: "clinic-fsa",
      label: "Within clinic FSA",
      title:
        asText(withinFsaTag.basis || withinFsaTag.Basis) ||
        "Patient postal FSA matches clinic postal FSA.",
      className: "ocf-detail-tag ocf-detail-tag--fsa",
    });
  } else if (outsideFsaTag) {
    tags.push({
      key: "clinic-fsa",
      label: "Outside clinic FSA",
      title:
        asText(outsideFsaTag.basis || outsideFsaTag.Basis) ||
        "Patient postal FSA differs from clinic postal FSA.",
      className: "ocf-detail-tag ocf-detail-tag--fsa",
    });
  } else {
    tags.push({
      key: "clinic-fsa",
      label: "Clinic FSA unknown",
      title: "Patient or clinic postal FSA was not available.",
      className: "ocf-detail-tag ocf-detail-tag--fsa",
    });
  }

  const newbornTag = getServerTag(detail, ["newborn"]);
  if (newbornTag) {
    tags.push({
      key: "newborn",
      label: "Newborn",
      title:
        asText(newbornTag.basis || newbornTag.Basis) ||
        "Patient age is under 1 year.",
      className: "ocf-detail-tag ocf-detail-tag--newborn",
    });
  }

  const priority = getNavigatorPriority(detail);
  tags.push({
    key: "priority",
    label: `${priority} priority`,
    title: isNavigatorAssisted(detail)
      ? "Navigator priority level returned with this forwarded request."
      : "Public/direct requests default to Routine priority unless otherwise tagged.",
    className: priorityPillClass(priority),
  });

  tags.push({
    key: "source",
    label: sourceDisplayLabel(detail),
    title: "Request source.",
    className: sourcePillClass(detail),
  });

  return tags;
}

function KeyValue({
  label,
  value,
}: {
  label: string;
  value: string | number | null | undefined;
}) {
  const safeValue =
    value === null || value === undefined || value === "" ? "—" : value;

  return (
    <div className="ocf-kv-item">
      <span className="k">{label}</span>
      <span className="v">{safeValue}</span>
    </div>
  );
}

export default function PatientRequestDetailsPage() {
  const navigate = useNavigate();
  const params = useParams();

  const requestId = useMemo(() => {
    const n = Number(params.requestId);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [params.requestId]);

  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<RequestDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [previewLoadingDecision, setPreviewLoadingDecision] =
    useState<DecisionValue | null>(null);
  const [previewLoadingFinalize, setPreviewLoadingFinalize] =
    useState<FinalizeAction | null>(null);
  const [submittingDecision, setSubmittingDecision] =
    useState<DecisionValue | null>(null);
  const [submittingFinalize, setSubmittingFinalize] =
    useState<FinalizeAction | null>(null);
  const [pendingDecision, setPendingDecision] = useState<DecisionValue | null>(
    null,
  );
  const [pendingFinalizeAction, setPendingFinalizeAction] =
    useState<FinalizeAction | null>(null);
  const [pendingFinalizeNote, setPendingFinalizeNote] = useState<string | null>(
    null,
  );
  const [decisionPreview, setDecisionPreview] =
    useState<DecisionEmailPreview | FinalizeEmailPreview | null>(null);

  const loadDetails = useCallback(async () => {
    if (!requestId) {
      setError("Invalid request ID.");
      setDetail(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await getFamilyDoctorRequestDetails(requestId);
      setDetail(data);
    } catch (e: any) {
      setError(
        e instanceof ApiError ? e.message : "Failed to load request details",
      );
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [requestId]);

  useEffect(() => {
    loadDetails();
  }, [loadDetails]);

  function closeDecisionPreview() {
    if (submittingDecision || submittingFinalize) return;
    setPendingDecision(null);
    setPendingFinalizeAction(null);
    setPendingFinalizeNote(null);
    setDecisionPreview(null);
  }

  async function onRequestDecisionPreview(decision: DecisionValue) {
    if (!detail?.requestId) return;

    setPreviewLoadingDecision(decision);
    setError(null);
    setSuccessMessage(null);
    setPendingDecision(null);
    setPendingFinalizeAction(null);
    setPendingFinalizeNote(null);
    setDecisionPreview(null);

    try {
      const preview = await previewFamilyDoctorRequestDecision(
        detail.requestId,
        decision,
      );
      setPendingDecision(decision);
      setDecisionPreview(preview);
    } catch (e: any) {
      setError(
        e instanceof ApiError
          ? e.message
          : "Failed to prepare the patient email preview",
      );
    } finally {
      setPreviewLoadingDecision(null);
    }
  }

  async function onConfirmDecision() {
    if (!detail?.requestId || !pendingDecision) return;

    const decision = pendingDecision;
    setSubmittingDecision(decision);
    setError(null);
    setSuccessMessage(null);

    try {
      const response: any = await decideFamilyDoctorRequest(
        detail.requestId,
        decision,
      );
      const autoClosedCountRaw = response?.autoClosedCount;
      const autoClosedCount =
        typeof autoClosedCountRaw === "number"
          ? autoClosedCountRaw
          : Number.isFinite(Number(autoClosedCountRaw))
            ? Number(autoClosedCountRaw)
            : 0;

      setSuccessMessage(
        decision === "Accepted" && autoClosedCount > 0
          ? `Meet and greet invitation sent. Also closed ${autoClosedCount} other active request${
              autoClosedCount === 1 ? "" : "s"
            } for this patient.`
          : decisionSavedMessage(decision),
      );

      setPendingDecision(null);
      setDecisionPreview(null);
      await loadDetails();
    } catch (e: any) {
      if (
        asText((e as any)?.payload?.code) ===
        MEET_AND_GREET_BACKLOG_LIMIT_CODE
      ) {
        setPendingDecision(null);
        setDecisionPreview(null);
      }
      setError(decisionErrorMessage(e, "Failed to apply decision"));
    } finally {
      setSubmittingDecision(null);
    }
  }

  async function onConfirmEmailPreview() {
    if (pendingFinalizeAction) {
      await onConfirmFinalizePreview();
      return;
    }

    await onConfirmDecision();
  }

  async function onConfirmFinalizePreview() {
    if (!detail?.requestId || !pendingFinalizeAction) return;

    const action = pendingFinalizeAction;
    const note = pendingFinalizeNote;
    setSubmittingFinalize(action);
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await finalizeFamilyDoctorRequest(
        detail.requestId,
        action,
        note ?? null,
      );
      setSuccessMessage(finalizeSavedMessage(action, response?.message));
      setPendingFinalizeAction(null);
      setPendingFinalizeNote(null);
      setDecisionPreview(null);
      await loadDetails();
    } catch (e: any) {
      setError(e instanceof ApiError ? e.message : "Failed to finalize request");
    } finally {
      setSubmittingFinalize(null);
    }
  }

  async function onRequestFinalizePreview(
    action: FinalizeAction,
    note?: string | null,
  ) {
    if (!finalizeActionRequiresEmailPreview(action)) {
      await onFinalize(action, note);
      return;
    }

    if (!detail?.requestId) return;

    setPreviewLoadingFinalize(action);
    setError(null);
    setSuccessMessage(null);
    setPendingDecision(null);
    setPendingFinalizeAction(null);
    setPendingFinalizeNote(null);
    setDecisionPreview(null);

    try {
      const preview = await previewFamilyDoctorRequestFinalize(
        detail.requestId,
        action,
        note ?? null,
      );
      setPendingFinalizeAction(action);
      setPendingFinalizeNote(note ?? null);
      setDecisionPreview(preview);
    } catch (e: any) {
      setError(
        e instanceof ApiError
          ? e.message
          : "Failed to prepare the patient email preview",
      );
    } finally {
      setPreviewLoadingFinalize(null);
    }
  }

  async function onFinalize(action: FinalizeAction, note?: string | null) {
    if (!detail?.requestId) return;

    setSubmittingFinalize(action);
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await finalizeFamilyDoctorRequest(
        detail.requestId,
        action,
        note ?? null,
      );
      setSuccessMessage(finalizeSavedMessage(action, response?.message));
      await loadDetails();
    } catch (e: any) {
      setError(e instanceof ApiError ? e.message : "Failed to finalize request");
    } finally {
      setSubmittingFinalize(null);
    }
  }

  const patientName = useMemo(
    () => patientDisplayName(detail?.patient),
    [detail?.patient],
  );
  const normalizedStatus = useMemo(
    () => normalizeStatus(detail?.status || ""),
    [detail?.status],
  );
  const statusKey = normalizedStatus.toLowerCase();
  const finalized = useMemo(() => isFinalized(detail), [detail]);
  const canFinalize = !!detail && !finalized && statusKey === "accepted";
  const canAcceptTop =
    !!detail &&
    !finalized &&
    (statusKey === "submitted" ||
      statusKey === "inreview" ||
      statusKey === "waitlist");
  const canWaitlistTop =
    !!detail &&
    !finalized &&
    (statusKey === "submitted" || statusKey === "inreview");
  const canDeclineTop =
    !!detail &&
    !finalized &&
    (statusKey === "submitted" ||
      statusKey === "inreview" ||
      statusKey === "waitlist");
  const decisionBusy =
    !!submittingDecision ||
    !!submittingFinalize ||
    !!previewLoadingDecision ||
    !!previewLoadingFinalize;
  const topTags = useMemo(() => buildTopTags(detail), [detail]);
  const requestForLabel = formatRequestFor(
    (detail as any)?.request?.requestFor ?? (detail as any)?.requestFor,
  );
  const activities = useMemo(() => {
    const d = detail as any;
    if (Array.isArray(d?.activities)) return d.activities;
    if (Array.isArray(d?.timeline?.activities)) return d.timeline.activities;
    return [];
  }, [detail]);

  return (
    <div className="ocf-request-detail-page">
      <div className="ocf-detail-top-actions">
        <button
          className="ocf-btn ocf-btn--secondary"
          type="button"
          onClick={() => navigate("/portal/family-doctor/requests")}
        >
          ← Back to requests
        </button>

        <button
          className="ocf-btn ocf-btn--secondary"
          type="button"
          onClick={loadDetails}
          disabled={loading}
          aria-busy={loading}
        >
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {loading ? (
        <div
          className="ocf-card ocf-detail-empty ocf-detail-empty--loading"
          role="status"
        >
          <span className="ocf-detail-empty-title">
            Loading request details…
          </span>
        </div>
      ) : error && !detail ? (
        <div className="ocf-card">
          <div className="ocf-error">{error}</div>
        </div>
      ) : !detail ? (
        <div className="ocf-card ocf-detail-empty">
          <span className="ocf-detail-empty-title">
            No request details found.
          </span>
        </div>
      ) : (
        <div className="ocf-request-detail-shell">
          <section className="ocf-request-detail-main">
            <div className="ocf-card ocf-request-hero">
              <div className="ocf-detail-header ocf-detail-header--stacked">
                <div className="ocf-detail-header-main">
                  <div className="ocf-page-eyebrow">Patient request details</div>
                  <div className="ocf-detail-title-row">
                    <h1 className="ocf-detail-title">{patientName}</h1>
                    <span className={statusPillClass(normalizedStatus)}>
                      {statusDisplayLabel(detail.status)}
                    </span>
                  </div>
                  <p className="ocf-detail-patient">
                    <span className="ocf-detail-request-id">
                      Request #{detail.requestId}
                    </span>
                  </p>

                  <div className="ocf-detail-tag-row" aria-label="Request tags">
                    {topTags.map((tag) => (
                      <span
                        key={tag.key}
                        className={tag.className || "ocf-detail-tag"}
                        title={tag.title}
                      >
                        {tag.label}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {canAcceptTop || canWaitlistTop || canDeclineTop ? (
              <section
                className="ocf-request-action-panel"
                aria-label="Clinic decision actions"
              >
                <div className="ocf-request-action-panel-copy">
                  <div className="ocf-request-action-panel-eyebrow">
                    Clinic decision
                  </div>
                  <p>
                    Choose the next request outcome. Patient emails are previewed
                    before they are sent.
                  </p>
                </div>

                <div className="ocf-decision-buttons">
                  {canAcceptTop ? (
                    <button
                      className="ocf-btn ocf-btn--good ocf-decision-btn ocf-decision-btn--accept"
                      type="button"
                      onClick={() => onRequestDecisionPreview("Accepted")}
                      disabled={decisionBusy}
                    >
                      <span className="ocf-decision-icon" aria-hidden="true">
                        ✓
                      </span>
                      {previewLoadingDecision === "Accepted"
                        ? "Preparing invitation…"
                        : submittingDecision === "Accepted"
                          ? "Sending invitation…"
                          : "Invite for Meet & Greet"}
                    </button>
                  ) : null}

                  {canWaitlistTop ? (
                    <button
                      className="ocf-btn ocf-btn--warn ocf-decision-btn ocf-decision-btn--waitlist"
                      type="button"
                      onClick={() => onRequestDecisionPreview("Waitlist")}
                      disabled={decisionBusy}
                    >
                      <span className="ocf-decision-icon" aria-hidden="true">
                        ⏱
                      </span>
                      {previewLoadingDecision === "Waitlist"
                        ? "Preparing email…"
                        : submittingDecision === "Waitlist"
                          ? "Waitlisting…"
                          : "Waitlist"}
                    </button>
                  ) : null}

                  {canDeclineTop ? (
                    <button
                      className="ocf-btn ocf-btn--danger ocf-decision-btn ocf-decision-btn--decline"
                      type="button"
                      onClick={() => onRequestDecisionPreview("Declined")}
                      disabled={decisionBusy}
                    >
                      <span className="ocf-decision-icon" aria-hidden="true">
                        ✕
                      </span>
                      {previewLoadingDecision === "Declined"
                        ? "Preparing email…"
                        : submittingDecision === "Declined"
                          ? "Declining…"
                          : "Decline"}
                    </button>
                  ) : null}
                </div>
              </section>
            ) : null}

            {error ? (
              <div className="ocf-error" role="alert">
                {error}
              </div>
            ) : null}

            {successMessage ? (
              <div className="ocf-notice ocf-notice--success" role="status">
                {successMessage}
              </div>
            ) : null}

            {statusKey === "closedacceptedelsewhere" ? (
              <div className="ocf-notice ocf-notice--info">
                This request was closed because the patient accepted elsewhere.
              </div>
            ) : null}
            {statusKey === "closedpatientwithdrew" ? (
              <div className="ocf-notice ocf-notice--info">
                This request was closed because the patient withdrew or declined
                to proceed.
              </div>
            ) : null}
            {statusKey === "closednoshow" ? (
              <div className="ocf-notice ocf-notice--info">
                This request was closed because the Meet &amp; Greet was not
                completed.
              </div>
            ) : null}
            {statusKey === "closedclinicdeclinedaftermeetgreet" ? (
              <div className="ocf-notice ocf-notice--info">
                This request was closed because the clinic declined after the
                Meet &amp; Greet stage.
              </div>
            ) : null}
            {statusKey === "attachmentcomplete" ? (
              <div className="ocf-notice ocf-notice--info">
                Attachment is complete. This request is closed.
              </div>
            ) : null}

            {canFinalize ? (
              <section
                className="ocf-request-action-panel ocf-request-action-panel--finalize"
                aria-label="Post-invitation outcome actions"
              >
                <div className="ocf-request-action-panel-copy">
                  <div className="ocf-request-action-panel-eyebrow">
                    Meet &amp; Greet outcome
                  </div>
                  <p>
                    Complete or close this request after the invitation stage.
                  </p>
                </div>

                <div className="ocf-request-action-panel-body">
                  <PatientRequestAttachmentOutcomeCard
                    currentStatus={detail.status}
                    disabled={decisionBusy}
                    submittingAction={submittingFinalize as any}
                    onFinalize={(action, note) =>
                      onRequestFinalizePreview(action as FinalizeAction, note)
                    }
                  />
                </div>
              </section>
            ) : null}

            <section className="ocf-section ocf-request-overview-card">
              <div className="ocf-request-overview-header">
                <div>
                  <h3 className="ocf-section-title">Request Overview</h3>
                  <p className="ocf-request-overview-subtitle">
                    Patient, applicant, request details, and navigator context
                    in one readable workspace.
                  </p>
                </div>
              </div>

              <div className="ocf-request-overview-stack">
                <section className="ocf-request-overview-group">
                  <div className="ocf-request-overview-group-header">
                    Patient Snapshot
                  </div>
                  <div className="ocf-kv ocf-request-overview-kv">
                    <KeyValue label="Name" value={patientName} />
                    <KeyValue
                      label="Gender"
                      value={(detail as any)?.patient?.gender}
                    />
                    <KeyValue
                      label="DOB"
                      value={formatDateOnly(detail.patient.dateOfBirth)}
                    />
                    <KeyValue
                      label="Postal code"
                      value={detail.patient.postalCode}
                    />
                    <KeyValue label="Request for" value={requestForLabel} />
                    <KeyValue
                      label="Coverage"
                      value={detail.request.coverageType?.name}
                    />
                  </div>
                  <p className="ocf-section-note">
                    Applicant contact is shown separately because the applicant
                    may not be the patient.
                  </p>
                </section>

                <section className="ocf-request-overview-group">
                  <div className="ocf-request-overview-group-header">
                    Applicant Contact
                  </div>
                  <div className="ocf-kv ocf-request-overview-kv">
                    <KeyValue
                      label="Email"
                      value={(detail as any)?.applicant?.email}
                    />
                    <KeyValue
                      label="Phone"
                      value={(detail as any)?.applicant?.phone}
                    />
                  </div>
                </section>

                <section className="ocf-request-overview-group">
                  <div className="ocf-request-overview-group-header">
                    Request Details
                  </div>
                  <div className="ocf-kv ocf-request-overview-kv">
                    <KeyValue label="Source" value={sourceDisplayLabel(detail)} />
                    <KeyValue
                      label="Submitted"
                      value={formatDateTime(detail.timeline.submittedAt)}
                    />
                    <KeyValue
                      label="Verified"
                      value={formatDateTime((detail as any)?.timeline?.verifiedAt)}
                    />
                  </div>

                  <div className="ocf-reasons-block">
                    <div className="k">MOH reason(s)</div>
                    {detail.reasons.length === 0 ? (
                      <div className="ocf-muted">—</div>
                    ) : (
                      <ul className="ocf-reasons">
                        {detail.reasons.map((reason) => (
                          <li key={reason.id}>{reason.name}</li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {detail.request.otherReasonText ? (
                    <div className="ocf-reasons-block">
                      <div className="k">Other non-medical reason</div>
                      <div className="ocf-note">
                        {detail.request.otherReasonText}
                      </div>
                    </div>
                  ) : null}
                </section>

                {isNavigatorAssisted(detail) ? (
                  <section className="ocf-request-overview-group ocf-request-overview-group--navigator">
                    <div className="ocf-request-overview-group-header">
                      Navigator Context
                    </div>
                    <div className="ocf-kv ocf-request-overview-kv">
                      <KeyValue
                        label="Source"
                        value={sourceDisplayLabel(detail)}
                      />
                      <KeyValue
                        label="Priority"
                        value={getNavigatorPriority(detail)}
                      />
                      <KeyValue
                        label="Forwarded by"
                        value={detail.navigator?.forwardedByNavigatorName}
                      />
                      <KeyValue
                        label="Forwarded"
                        value={formatDateTime(detail.navigator?.forwardedAt)}
                      />
                    </div>

                    <div className="ocf-reasons-block">
                      <div className="k">Navigator summary</div>
                      <div className="ocf-note">
                        {detail.navigator?.publicSummary ||
                          "This request was forwarded through the Navigator workflow for clinic review."}
                      </div>
                    </div>
                  </section>
                ) : null}
              </div>
            </section>

            <details className="ocf-section ocf-status-timeline-details">
              <summary className="ocf-status-timeline-summary">
                <span className="ocf-section-title ocf-status-timeline-title">
                  Status Timeline
                </span>
                <span className="ocf-status-timeline-hint">
                  Click to expand
                </span>
              </summary>
              <div className="ocf-kv ocf-status-timeline-body">
                <KeyValue
                  label="Created"
                  value={formatDateTime(detail.timeline.createdAt)}
                />
                <KeyValue
                  label="Submitted"
                  value={formatDateTime(detail.timeline.submittedAt)}
                />
                <KeyValue
                  label="Verified"
                  value={formatDateTime((detail as any)?.timeline?.verifiedAt)}
                />
                <KeyValue
                  label="Decision"
                  value={formatDateTime(detail.timeline.decisionAt)}
                />
                <KeyValue
                  label="Forwarded"
                  value={formatDateTime((detail as any)?.timeline?.forwardedAt)}
                />
                <KeyValue
                  label="Attachment completed"
                  value={formatDateTime(detail.timeline.attachmentCompletedAt)}
                />
                <KeyValue
                  label="Cancelled / closed"
                  value={formatDateTime(detail.timeline.cancelledAt)}
                />
                <KeyValue
                  label="Auto-expired"
                  value={formatDateTime(detail.timeline.autoExpiredAt)}
                />
              </div>
            </details>

            {finalized ? (
              <p className="ocf-detail-finalized-note">
                This request is finalized and can’t be changed.
              </p>
            ) : null}
          </section>

          <aside className="ocf-detail-activity-column">
            <div className="ocf-detail-activity-inner">
              <p className="ocf-detail-activity-note">
                These entries are recorded by the current Family Doctor user, do
                not change request status, and do not send patient emails.
              </p>

              <PatientRequestActivityPanel
                requestId={detail.requestId}
                currentStatus={detail.status}
                actorRole="FamilyDoctor"
                initialActivities={activities}
                disabled={decisionBusy || !!submittingFinalize}
                onActivityAdded={loadDetails}
              />
            </div>
          </aside>
        </div>
      )}

      {decisionPreview && (pendingDecision || pendingFinalizeAction) ? (
        <div
          className="ocf-modal-backdrop ocf-decision-preview-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeDecisionPreview();
          }}
        >
          <div
            className="ocf-modal ocf-decision-preview-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ocf-decision-preview-title"
          >
            <div className="ocf-modal-header">
              <div>
                <h2 id="ocf-decision-preview-title" className="ocf-modal-title">
                  Confirm email to patient
                </h2>
                <p className="ocf-modal-subtitle">
                  Review the exact email before you{" "}
                  {pendingFinalizeAction
                    ? finalizeActionLabel(pendingFinalizeAction).toLowerCase()
                    : pendingDecision
                      ? decisionActionLabel(pendingDecision).toLowerCase()
                      : "continue"}
                  .
                </p>
              </div>

              <button
                className="ocf-modal-close"
                type="button"
                onClick={closeDecisionPreview}
                disabled={!!submittingDecision || !!submittingFinalize}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="ocf-email-preview-meta">
              <div>
                <span className="k">Action</span>
                <span className="v">
                  {pendingFinalizeAction
                    ? finalizeActionLabel(pendingFinalizeAction)
                    : pendingDecision
                      ? decisionActionLabel(pendingDecision)
                      : "—"}
                </span>
              </div>
              <div>
                <span className="k">To</span>
                <span className="v">{decisionPreview.email?.to || "—"}</span>
              </div>
              <div>
                <span className="k">Subject</span>
                <span className="v">
                  {decisionPreview.email?.subject || "—"}
                </span>
              </div>
            </div>

            {!decisionPreview.email?.to ? (
              <div className="ocf-email-preview-warning">
                No patient/applicant email was found. The request can still be
                updated, but no email will be sent.
              </div>
            ) : null}

            <div className="ocf-email-preview-block">
              <div className="ocf-email-preview-label">Email body</div>
              <pre className="ocf-email-preview-body">
                {decisionPreview.email?.text || ""}
              </pre>
            </div>

            <div className="ocf-modal-actions">
              <button
                className="ocf-btn ocf-btn--secondary"
                type="button"
                onClick={closeDecisionPreview}
                disabled={!!submittingDecision || !!submittingFinalize}
              >
                Cancel
              </button>

              <button
                className={
                  pendingFinalizeAction === "MovedToWaitlist" ||
                  pendingDecision === "Waitlist"
                    ? "ocf-btn ocf-btn--warn"
                    : pendingDecision === "Declined"
                      ? "ocf-btn ocf-btn--danger"
                      : "ocf-btn ocf-btn--good"
                }
                type="button"
                onClick={onConfirmEmailPreview}
                disabled={!!submittingDecision || !!submittingFinalize}
              >
                {submittingDecision || submittingFinalize
                  ? "Sending…"
                  : pendingFinalizeAction
                    ? finalizeConfirmLabel(pendingFinalizeAction)
                    : pendingDecision
                      ? decisionConfirmLabel(pendingDecision)
                      : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}